/**
 * Typed search params for the `/activity` (read-only audit feed) route. The event-type
 * filter, the date range, and the search text live in the URL so they are deep-linkable AND
 * compile-checked (the reports/employees `searchParams` pattern). TanStack Router validates
 * the raw URL params through `validateActivitySearch` on every navigation.
 *
 * `?type=<eventType>` narrows the feed to one action type (driving the type-filter pills).
 * `?from=YYYY-MM-DD` / `?to=YYYY-MM-DD` bound the date range. `?q=<text>` filters by the
 * event description (server-side LIKE). All are forwarded to the READ-ONLY coarse GET — none
 * mutate (Pitfall 3).
 */

export interface ActivitySearch {
  type?: string;
  actorUserId?: string;
  from?: string;
  to?: string;
  q?: string;
}

/** Coerce a raw URL value into a trimmed, length-capped string (else undefined). */
function asText(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().slice(0, max);
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Coerce a raw URL value into a YYYY-MM-DD date (else undefined). */
function asDate(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : undefined;
}

/**
 * TanStack Router `validateSearch` for the activity feed route. Keeps each filter only when
 * present + well-formed, so the canonical URL stays clean (`/activity` vs
 * `/activity?type=invoice.created` vs `/activity?from=2026-01-01&to=2026-06-30`).
 */
export function validateActivitySearch(raw: Record<string, unknown>): ActivitySearch {
  const search: ActivitySearch = {};
  const type = asText(raw.type, 120);
  if (type) search.type = type;
  const actorUserId =
    typeof raw.actorUserId === 'string' && /^\d+$/.test(raw.actorUserId)
      ? raw.actorUserId
      : undefined;
  if (actorUserId) search.actorUserId = actorUserId;
  const from = asDate(raw.from);
  if (from) search.from = from;
  const to = asDate(raw.to);
  if (to) search.to = to;
  const q = asText(raw.q, 100);
  if (q) search.q = q;
  return search;
}
